/**
 * Ory Kratos client configuration and utilities.
 */

import { Configuration, FrontendApi } from '@ory/client';
import { config } from '../config';

// Create Kratos frontend API client
const kratosConfig = new Configuration({
  basePath: config.kratosPublicUrl,
  baseOptions: {
    withCredentials: true,
  },
});

export const kratos = new FrontendApi(kratosConfig);

/**
 * Get flow ID from URL search params.
 */
export function getFlowId(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get('flow');
}

/**
 * Get return URL from URL search params.
 */
export function getReturnTo(search: string): string {
  const params = new URLSearchParams(search);
  return params.get('return_to') || config.defaultReturnUrl;
}

/**
 * Redirect to a return URL, handling both internal paths and external URLs.
 * External URLs (http/https) use window.location.href since React Router
 * can only handle internal paths.
 */
export async function redirectTo(url: string, navigate: (path: string, opts?: { replace?: boolean }) => void): Promise<void> {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if this is a cross-domain redirect (different base domain)
    try {
      const target = new URL(url);
      const current = new URL(window.location.href);
      const isSameDomain = target.hostname === current.hostname ||
        target.hostname.endsWith('.' + current.hostname.split('.').slice(-2).join('.'));

      if (!isSameDomain) {
        // Cross-domain: get a one-time exchange code and append to URL
        const gatewayUrl = config.gatewayUrl;
        const resp = await fetch(`${gatewayUrl}/v1/auth/exchange-code`, {
          method: 'POST',
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          target.searchParams.set('code', data.code);
          window.location.href = target.toString();
          return;
        }
      }
    } catch {
      // Fall through to direct redirect
    }
    window.location.href = url;
  } else {
    navigate(url, { replace: true });
  }
}

/**
 * Build login URL with optional return_to.
 */
export function buildLoginUrl(returnTo?: string): string {
  const base = '/login';
  if (returnTo) {
    return `${base}?return_to=${encodeURIComponent(returnTo)}`;
  }
  return base;
}

/**
 * Extract error messages from Kratos UI nodes.
 */
export function extractErrors(nodes: Array<{ messages?: Array<{ text: string }> }>): string[] {
  const errors: string[] = [];
  for (const node of nodes) {
    if (node.messages) {
      for (const message of node.messages) {
        errors.push(message.text);
      }
    }
  }
  return errors;
}

/**
 * Handle Kratos error responses.
 */
export function handleKratosError(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: { message?: string } } } }).response;
    if (response?.data?.error?.message) {
      return response.data.error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
